// inv/src/app/app-components/deals-modules/dealsFilter.jsx
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { DatePicker } from "../../../components/ui/datePicker";
import UniqueRoundsList from './uniqueRoundsList';
import AmountRange from './AmountRange';
import IndustryCategoryPicker from './IndustryCategoryPicker';

const DealsFilter = ({ totalResults, onFilterChange }) => {
  const { t } = useTranslation();

  // Local filter states
  const [selectedRounds, setSelectedRounds] = useState([]);
  const [selectedAmountFrom, setSelectedAmountFrom] = useState(null);
  const [selectedAmountTo, setSelectedAmountTo] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const keywordInputRef = useRef(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // ✅ NEW: industry categories
  const [industryCategories, setIndustryCategories] = useState([]);

  // Format total count
  const formatTotalCount = (totalResults) =>
    totalResults ? Number(totalResults).toLocaleString() : '';

  // Effect to trigger filter change
  useEffect(() => {
    const filters = {
      selectedRounds,
      investmentAmountFrom: selectedAmountFrom,
      investmentAmountTo: selectedAmountTo,
      keyword,
      fromDate,
      toDate,

      // ✅ NEW:
      industryCategories,
    };
    onFilterChange(filters);
  }, [selectedRounds, selectedAmountFrom, selectedAmountTo, keyword, fromDate, toDate, industryCategories]);

  // --- keyword handlers (as before) ---
  const handleKeywordKeyDown = (event) => {
    if (event.key === 'Enter') applyKeywordFilter();
  };
  const applyKeywordFilter = () => {
    if (keywordInput.trim()) {
      setKeyword(keywordInput.trim());
      setKeywordInput('');
    }
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (keywordInputRef.current && !keywordInputRef.current.contains(event.target)) {
        applyKeywordFilter();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [keywordInput]);

  const handleAmountChange = (amountFrom, amountTo) => {
    setSelectedAmountFrom(amountFrom);
    setSelectedAmountTo(amountTo);
  };
  const resetDateFilter = () => {
    setFromDate(null);
    setToDate(null);
  };

  // ===== Quick ranges helpers =====
  const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const endOfDay = (d) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  };
  const addDays = (d, delta) => {
    const x = new Date(d);
    x.setDate(x.getDate() + delta);
    return x;
  };
  const setRange = (from, to) => {
    setFromDate(from);
    setToDate(to);
  };

  const today = new Date();
  const quickRanges = [
    {
      key: 'yesterday',
      label: 'Yesterday',
      get: () => {
        const y = addDays(today, -1);
        return { from: startOfDay(y), to: endOfDay(y) };
      },
    },
    {
      key: 'last7',
      label: 'Last 7 days',
      get: () => ({
        // including today -> last 7 calendar days
        from: startOfDay(addDays(today, -6)),
        to: endOfDay(today),
      }),
    },
    {
      key: 'last30',
      label: 'Last 30 days',
      get: () => ({
        from: startOfDay(addDays(today, -29)),
        to: endOfDay(today),
      }),
    },
  ];

  const isSameDay = (a, b) =>
    a && b && a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const isActiveRange = (key) => {
    const { from, to } = quickRanges.find(r => r.key === key).get();
    return (
      fromDate &&
      toDate &&
      isSameDay(startOfDay(fromDate), startOfDay(from)) &&
      isSameDay(endOfDay(toDate), endOfDay(to))
    );
  };

  return (
    <>
      <div className="px-2 mb-6">
        <div className="mt-4">
          <span className="font-bold">{t('app:deals.t1')}</span> {formatTotalCount(totalResults)}
        </div>

        {/* Series/Round selection */}
        <div className="mt-4">
          <label className="block mb-2 text-sm font-medium">
            {t('app:deals.t2')}
          </label>
          <UniqueRoundsList
            selectedRounds={selectedRounds}
            setSelectedRounds={setSelectedRounds}
          />
        </div>

        {/* ✅ NEW: Industry categories */}
        <div className="mt-4">
          <label className="block mb-2 text-sm font-medium">
          {t('app:deals.t10')}
          </label>
          <IndustryCategoryPicker
            selectedCategories={industryCategories}
            setSelectedCategories={setIndustryCategories}
            placeholder={t('app:deals.t11')}
          />
        </div>

        {/* Investment Amount selection */}
        <AmountRange
          selectedAmountFrom={selectedAmountFrom}
          selectedAmountTo={selectedAmountTo}
          onAmountChange={handleAmountChange}
        />

        {/* Keyword input */}
        <div className="mt-4" ref={keywordInputRef}>
          <label className="block mb-2 text-sm font-medium">
            {t('app:deals.t5')}
          </label>
          <Input
            type="text"
            placeholder="E.g. 'Facebook'"
            selectedValues={keyword ? [keyword] : []}
            onAddValue={(value) => setKeyword(value)}
            onRemoveValue={() => setKeyword("")}
            onKeyDown={handleKeywordKeyDown}
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
          />
        </div>

        {/* Date Range Selection */}
        <div className="mt-4">
          <div className="flex items-center gap-x-3 mb-2">
            <label className="text-sm font-medium">{t('app:deals.t6')}</label>
            {(fromDate || toDate) && (
              <Button
                variant="link"
                size="xs"
                onClick={resetDateFilter}
                className="text-xs py-1 px-2 text-red-500 hover:text-red-700"
                title="Reset dates"
              >
                {t('app:other.t1')}
              </Button>
            )}
          </div>



          <div className="flex">
            <div className="mr-2">
              <DatePicker
                onDateChange={(date) => setFromDate(date)}
                selectedDate={fromDate}
                placeholder="From Date"
              />
            </div>
            <div>
              <DatePicker
                onDateChange={(date) => setToDate(date)}
                selectedDate={toDate}
                placeholder="To Date"
              />
            </div>
          </div>
        </div>
        {/* Quick ranges */}
        <div className="flex flex-wrap gap-3 mt-4 mb-4">
          {quickRanges.map((r) => {
            const active = isActiveRange(r.key);
            return (
              <span
                key={r.key}
                role="button"
                tabIndex={0}
                title={r.label}
                onClick={() => {
                  const { from, to } = r.get();
                  setRange(from, to);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const { from, to } = r.get();
                    setRange(from, to);
                  }
                }}
                className={[
                  "text-xs cursor-pointer underline underline-offset-4",
                  "hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-stone-300 rounded-sm px-0.5",
                  active ? "text-stone-900 font-semibold" : "text-stone-600"
                ].join(' ')}
              >
                {r.label}
              </span>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default DealsFilter;
